import React from 'react'

import './styles.scss'

import chartData from '../../../data/chartData.json'
import useSize from 'hooks/useSize'
import { scaleLog } from 'd3-scale'
import { line, curveBasis } from 'd3-shape'

const f = new Intl.NumberFormat('de-CH')

type DisplayData = {
  code: string
  name: string
  all: number[]
  dailyNew: number[]
  death: number[]
  dailyDeath: number[]
  active: boolean
  highlighted: boolean
}

type Props = {
  showHint?: boolean
  legendLabel: string
  x: string
  y: string
  legendScale?: number
  highlighted?: {
    [key: string]: {
      color: string
      align?: 'top' | 'bottom' | undefined
      labelOptional?: boolean
    }
  }
  hidden: string[]
}

export const LogLogLine: React.FC<Props> = ({
  showHint,
  highlighted = {},
  hidden = [],
  legendScale = 1,
  legendLabel = 'Fälle',
  x = 'all',
  y = 'dailyNew'
}) => {
  const [container, size] = useSize()

  const [active, setActive] = React.useState<string | null>(null)

  const width = size.width
  const height = size.isMobile ? 350 : 450

  const lineStrokeWidth = size.isMobile ? 2 : 2

  const gutterLeft = size.isMobile ? 1 : 60
  const gutterBottom = 20
  const plotArea = {
    top: 0,
    right: width,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xDomain = [1000 * legendScale, 4e6 * legendScale]

  const xAxisScale = React.useMemo(
    () =>
      scaleLog()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size, xDomain]
  )

  const yDomain = [1 * legendScale, 1e5 * legendScale]

  const yAxisScale = React.useMemo(() => {
    const scale = scaleLog()
      .domain(yDomain)
      .range([plotArea.bottom, plotArea.top])

    return x => (x > 0 ? scale(x) : plotArea.bottom)
  }, [size, yDomain])

  const ticks = [1, 10, 1e2, 1e3, 1e4, 1e5, 1e6].map(t => t * legendScale)

  const lineFunction = React.useMemo(
    () =>
      line<{ x: number; y: number }>()
        .x(d => xAxisScale(d.x))
        .y(d => yAxisScale(d.y))
        .curve(curveBasis),
    [size]
  )

  const displayData: DisplayData[] = chartData
    .filter(d => !hidden.includes(d.code))
    .map(d => ({
      ...d,
      active: d.code === active,
      highlighted: Object.keys(highlighted).some(k => k === d.code)
    }))
    .reduce<[DisplayData[], DisplayData[], DisplayData[]]>(
      (acc, cur) => {
        if (cur.active) {
          acc[0].push(cur)
        } else if (cur.highlighted) {
          acc[active ? 2 : 1].push(cur)
        } else {
          acc[active ? 1 : 2].push(cur)
        }
        return acc
      },
      [[], [], []]
    )
    .map(l => l.sort())
    .reduce((acc, cur) => cur.concat(acc), [])

  return (
    <div className='chart'>
      <div
        ref={container}
        className='ui container'
        onTouchStart={e => e.touches.length < 2 && setActive(null)}
        onMouseLeave={() => !size.isMobile && setActive(null)}
        style={{ position: 'relative' }}
      >
        {showHint && (
          <p className='chartBox'>
            <strong>Worauf Sie achten sollten:</strong>
            <br />
            Sobald die Linien nach unten ziehen, wird der exponentielle
            Wachstums&shy;pfad verlassen.
            <br />
            <div>
              <i className='icon bullseye' />
              <span>Berühren Sie die Kurven, um Länder hervorzuheben.</span>
            </div>
          </p>
        )}
        <svg width={width} height={height} style={{ backgroundColor: '#fff' }}>
          {
            // mouseTrap
          }
          {!size.isMobile && (
            <polygon
              points={`${plotArea.left},${plotArea.bottom} ${plotArea.right *
                0.15},${plotArea.bottom} ${plotArea.right},${plotArea.bottom *
                0.1} ${plotArea.right},${plotArea.top} ${plotArea.right *
                0.95},${plotArea.top} ${plotArea.left},${plotArea.bottom *
                0.95}`}
              fill='#aaa'
              opacity={0}
              onMouseLeave={() => setActive(null)}
              cursor='pointer'
            />
          )}
          {
            // x-axis
          }
          <line
            x1={plotArea.left}
            x2={plotArea.right}
            y1={plotArea.bottom}
            y2={plotArea.bottom}
            strokeWidth={1}
            stroke='#f1f1f3'
            pointerEvents='none'
          />
          {ticks.map((v, i) => (
            <g key={v}>
              {xAxisScale(v) < plotArea.right && (
                <line
                  x1={xAxisScale(v)}
                  x2={xAxisScale(v)}
                  y1={plotArea.top}
                  y2={plotArea.bottom}
                  strokeWidth={1}
                  stroke='#f1f1f3'
                  pointerEvents='none'
                />
              )}
              {xAxisScale(v) > plotArea.left && (
                <text
                  x={xAxisScale(v)}
                  y={plotArea.bottom + 15}
                  textAnchor='end'
                >
                  {f.format(v)}
                </text>
              )}
            </g>
          ))}
          {
            // y-axis
          }
          <line
            x1={plotArea.left}
            x2={plotArea.left}
            y1={plotArea.top}
            y2={plotArea.bottom}
            strokeWidth={1}
            stroke='#f1f1f3'
            pointerEvents='none'
          />
          {ticks.map((v, i) => (
            <g key={v}>
              {yAxisScale(v) > 0 && (
                <line
                  x1={plotArea.left}
                  x2={plotArea.right}
                  y1={yAxisScale(v)}
                  y2={yAxisScale(v)}
                  strokeWidth={1}
                  stroke='#f1f1f3'
                  pointerEvents='none'
                />
              )}
              {yAxisScale(v) < plotArea.bottom && (
                <text
                  y={yAxisScale(v)}
                  x={plotArea.left + (size.isMobile ? +3 : -3)}
                  alignmentBaseline='hanging'
                  textAnchor={size.isMobile ? 'start' : 'end'}
                  pointerEvents='none'
                >
                  {f.format(v)}
                </text>
              )}
            </g>
          ))}
          <g transform='translate(0 0)'>
            {
              // lines
            }
            {displayData.map(d => {
              const points = d[y].map((e, i) => ({
                x: d[x][i],
                y: e
              }))

              const maxIdx = d[y].length - 1

              return (
                <g key={d.code}>
                  <path
                    onMouseEnter={() => setActive(d.code)}
                    stroke={
                      d.highlighted && !active
                        ? highlighted[d.code].color
                        : d.active
                        ? '#ECAF1B'
                        : '#e1e1e1'
                    }
                    strokeWidth={lineStrokeWidth}
                    fill='none'
                    d={lineFunction(points)?.toString()}
                    cursor='pointer'
                  />
                  {
                    // markers
                  }
                  <circle
                    onMouseEnter={() => setActive(d.code)}
                    cx={xAxisScale(d[x][maxIdx])}
                    cy={yAxisScale(d[y][maxIdx])}
                    fill={
                      d.highlighted && !active
                        ? highlighted[d.code].color
                        : d.active
                        ? '#ECAF1B'
                        : '#e1e1e1'
                    }
                    r={lineStrokeWidth * 3}
                    cursor='pointer'
                  />
                </g>
              )
            })}
            {
              // reference line
            }
            <g transform='translate(-20,-20)'>
              <line
                x1={xAxisScale(10) + 20}
                y1={yAxisScale(1)}
                x2={xAxisScale(1e6)}
                y2={yAxisScale(1e5)}
                stroke='#999'
                strokeDasharray='4'
              />
              <g
                transform={`translate(${xAxisScale(
                  3e4 * legendScale
                )},${yAxisScale(2.5e3 * legendScale)}) rotate(${(-Math.atan(
                  yAxisScale(0) / (xAxisScale(1e6) - xAxisScale(10))
                ) *
                  180) /
                  Math.PI}) `}
              >
                <text y={-height * 0.02} textAnchor='middle'>
                  Unkontrolliertes {size.isMobile ? '' : 'exponentielles'}{' '}
                  Wachstum
                </text>
              </g>
            </g>
            {
              // marker labels
            }
            {displayData.map(d => {
              const maxIdx = d[y].length - 1

              const labelConfig = highlighted[d.code]

              const xVal = xAxisScale(d[x][maxIdx])
              const closeToRightEdge = xVal > plotArea.right * 0.9

              return (
                <g key={d.code}>
                  {(d.active ||
                    (d.highlighted && !labelConfig?.labelOptional)) && (
                    <g
                      onMouseEnter={() => setActive(d.code)}
                      onClick={() => setActive(d.code)}
                      cursor='pointer'
                      opacity={active && d.highlighted && !d.active ? 0.3 : 1}
                      transform={`translate(0, ${
                        labelConfig?.align === 'top'
                          ? -7
                          : labelConfig?.align === 'bottom'
                          ? 7
                          : 0
                      })`}
                    >
                      <text
                        x={xVal + (closeToRightEdge ? 5 : +9)}
                        y={
                          yAxisScale(d[y][maxIdx]) + (closeToRightEdge ? 22 : 5)
                        }
                        textAnchor={closeToRightEdge ? 'end' : 'start'}
                        strokeWidth={4}
                        stroke='#fff'
                        opacity={0.8}
                        className='countryLabel'
                      >
                        {d.name}
                      </text>
                      <text
                        x={xVal + (closeToRightEdge ? 5 : +9)}
                        y={
                          yAxisScale(d[y][maxIdx]) + (closeToRightEdge ? 22 : 5)
                        }
                        textAnchor={closeToRightEdge ? 'end' : 'start'}
                        className='countryLabel'
                      >
                        {d.name}
                      </text>
                    </g>
                  )}
                </g>
              )
            })}
            {
              // labels
            }
            <g>
              <text
                className='axisLabel'
                pointerEvents='none'
                alignmentBaseline='hanging'
                x={gutterLeft + 3}
                y={size.isMobile ? 15 : 0}
              >
                Neue {`${legendLabel}`} (Ø 7 Tage)
              </text>
              <text
                className='axisLabel'
                pointerEvents='none'
                x={plotArea.right}
                y={plotArea.bottom - 5}
                textAnchor='end'
              >
                {`${legendLabel}`} insgesamt
              </text>
            </g>
          </g>
        </svg>
      </div>
    </div>
  )
}
